/**
 * Shared pointer-hold activation for custom (non-dnd-kit) drag pipelines.
 * Mirrors {@link useSqDndSensors}: mouse → distance; touch → press-hold + tolerance.
 */

import {
	SQ_DND_POINTER_DISTANCE_PX,
	SQ_DND_TOUCH_DELAY_MS,
	SQ_DND_TOUCH_TOLERANCE_PX,
} from "./sq-dnd-sensors";
import { triggerSqDndDragStartHaptic } from "./sq-dnd-haptic";

export interface SqPointerHoldBeginArgs {
	pointerId: number;
	pointerType: string;
	clientX: number;
	clientY: number;
	/** Fired once when drag activates (distance or hold). */
	onActivate: () => void;
}

export type SqPointerHoldMoveResult =
	| "idle"
	| "pending"
	| "activated"
	| "tracking"
	| "cancelled";

/**
 * Controller for one in-flight press → drag gesture.
 * Call {@link begin} on pointerdown, {@link move} on pointermove, {@link end}/{@link cancel} on up/cancel.
 */
export class SqPointerHoldController {
	private pending: {
		pointerId: number;
		pointerType: string;
		originX: number;
		originY: number;
		active: boolean;
		onActivate: () => void;
		holdTimer: ReturnType<typeof setTimeout> | null;
	} | null = null;

	get isPending(): boolean {
		return this.pending != null && !this.pending.active;
	}

	get isActive(): boolean {
		return this.pending?.active === true;
	}

	get hasSession(): boolean {
		return this.pending != null;
	}

	begin(args: SqPointerHoldBeginArgs): void {
		this.cancel();
		const isTouch = args.pointerType === "touch";
		this.pending = {
			pointerId: args.pointerId,
			pointerType: args.pointerType,
			originX: args.clientX,
			originY: args.clientY,
			active: false,
			onActivate: args.onActivate,
			holdTimer: null,
		};
		if (isTouch) {
			this.pending.holdTimer = setTimeout(() => {
				const p = this.pending;
				if (p == null || p.active) {
					return;
				}
				this.activate(p);
			}, SQ_DND_TOUCH_DELAY_MS);
		}
	}

	move(clientX: number, clientY: number): SqPointerHoldMoveResult {
		const p = this.pending;
		if (p == null) {
			return "idle";
		}
		const dx = clientX - p.originX;
		const dy = clientY - p.originY;
		const distSq = dx * dx + dy * dy;

		if (p.active) {
			return "tracking";
		}

		if (p.pointerType === "touch") {
			const tol = SQ_DND_TOUCH_TOLERANCE_PX;
			if (distSq > tol * tol) {
				this.cancel();
				return "cancelled";
			}
			return "pending";
		}

		const dist = SQ_DND_POINTER_DISTANCE_PX;
		if (distSq < dist * dist) {
			return "pending";
		}
		this.activate(p);
		return "activated";
	}

	/** Returns whether the drag was active when the gesture ended. */
	end(): boolean {
		const wasActive = this.pending?.active === true;
		this.clearTimerOnly();
		this.pending = null;
		return wasActive;
	}

	cancel(): void {
		this.clearTimerOnly();
		this.pending = null;
	}

	private activate(p: NonNullable<typeof this.pending>): void {
		this.clearTimerOnly();
		if (p.active) {
			return;
		}
		p.active = true;
		if (p.pointerType === "touch") {
			triggerSqDndDragStartHaptic();
		}
		p.onActivate();
	}

	private clearTimerOnly(): void {
		const t = this.pending?.holdTimer;
		if (t != null) {
			clearTimeout(t);
			if (this.pending) {
				this.pending.holdTimer = null;
			}
		}
	}
}
